import { Icon, cn, type IconName } from "@sagot/ui";
import { useEffect, useRef } from "react";
import { safeHref } from "../markdown";
import { RESULT_STATE } from "./constants";
import type { ResultCard } from "./contract";
import { formatDate, formatTimeRange, zoneName } from "./dates";
import { downloadIcs } from "./ics";
import { DEFAULT_ACTION_LABELS, type ActionLabels } from "./labels";

interface Tone {
  icon: IconName;
  box: string;
  text: string;
  label: (labels: ActionLabels) => string;
}

const TONES: Record<string, Tone> = {
  [RESULT_STATE.sent]: { icon: "check-circle", box: "bg-success-soft", text: "text-success", label: (l) => l.stateSent },
  [RESULT_STATE.confirmed]: { icon: "check-circle", box: "bg-success-soft", text: "text-success", label: (l) => l.stateConfirmed },
  [RESULT_STATE.requested]: { icon: "clock", box: "bg-warning-soft", text: "text-warning", label: (l) => l.stateRequested },
  [RESULT_STATE.failed]: { icon: "alert-triangle", box: "bg-danger-soft", text: "text-danger", label: (l) => l.stateFailed },
};
const NEUTRAL_TONE: Tone = { icon: "check-circle", box: "bg-success-soft", text: "text-success", label: (l) => l.completed };

function toneOf(result: ResultCard): Tone {
  if (result.kind === "failed") return TONES[RESULT_STATE.failed] ?? NEUTRAL_TONE;
  return TONES[result.state] ?? NEUTRAL_TONE;
}

const LINK_CLASS = "btn btn-secondary btn-sm min-h-11 no-underline";

export interface ResultCardProps {
  result: ResultCard;
  /** English by default. */
  labels?: ActionLabels;
  /** The visitor's zone: the booked time is shown in it, with its name. */
  timeZone: string;
  locale?: string;
  /** Moves focus here when the result replaces a form the visitor just submitted. */
  focusOnMount?: boolean;
}

function When({ result, timeZone, locale }: Pick<ResultCardProps, "result" | "timeZone" | "locale">) {
  if (!result.start) return null;
  const range = formatTimeRange(result.start, result.end, timeZone, locale);
  return (
    <p className="flex items-start gap-1.5 font-semibold">
      <Icon name="calendar" className="mt-0.5 size-3.5 text-fg-subtle" />
      <span>
        {formatDate(result.start, timeZone, locale)} · {range} {zoneName(result.start, timeZone, locale)}
      </span>
    </p>
  );
}

function CalendarAction({ result, labels }: { result: ResultCard; labels: ActionLabels }) {
  const calendarHref = result.calendar_url ? safeHref(result.calendar_url) : null;
  if (result.ics) {
    const { ics } = result;
    return (
      <button
        type="button"
        className={LINK_CLASS}
        onClick={() => {
          downloadIcs(ics);
        }}
      >
        <Icon name="download" />
        {labels.addToCalendar}
      </button>
    );
  }
  if (!calendarHref) return null;
  return (
    <a className={LINK_CLASS} href={calendarHref} target="_blank" rel="noopener noreferrer">
      <Icon name="calendar" />
      {labels.addToCalendar}
    </a>
  );
}

/** What happened, in words + icon + colour: lead sent, appointment requested (not confirmed), booked, or failed. */
export function ResultCardView({ result, labels = DEFAULT_ACTION_LABELS, timeZone, locale, focusOnMount }: ResultCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const tone = toneOf(result);
  const meetHref = result.meet_url ? safeHref(result.meet_url) : null;
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);
  return (
    <div ref={ref} role="status" aria-live="polite" tabIndex={-1} className={cn("mt-2 flex items-start gap-2.5 rounded-lg border border-line p-3 outline-none focus-visible:ring-2 focus-visible:ring-primary", tone.box)}>
      <Icon name={tone.icon} className={cn("mt-0.5 size-5", tone.text)} />
      <div className="min-w-0 flex-1 space-y-2 text-xs text-fg">
        <div>
          <p className={cn("text-[11px] font-semibold uppercase", tone.text)}>{tone.label(labels)}</p>
          <p className="text-sm font-bold">{result.title}</p>
          {result.body ? <p className="mt-0.5 text-fg-muted">{result.body}</p> : null}
        </div>
        <When result={result} timeZone={timeZone} locale={locale} />
        {result.location ? (
          <p className="flex items-start gap-1.5">
            <Icon name="map-pin" className="mt-0.5 size-3.5 text-fg-subtle" />
            <span>{result.location}</span>
          </p>
        ) : null}
        {meetHref || result.ics || result.calendar_url ? (
          <div className="flex flex-wrap gap-2">
            {meetHref ? (
              <a className={LINK_CLASS} href={meetHref} target="_blank" rel="noopener noreferrer">
                <Icon name="external-link" />
                {labels.joinMeet}
              </a>
            ) : null}
            <CalendarAction result={result} labels={labels} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
